import { AppSettings, BaseStore } from '../Infrastructure';
import { action, observable } from 'mobx';
import { StringResources } from 'src/Shared/Constants';
import { AuthenticationResult, EventMessage, EventType, PublicClientApplication, SilentRequest } from '@azure/msal-browser';

export class AuthStore extends BaseStore {
  @observable public isLoggedIn = false;
  private _appSettings: AppSettings;

  constructor() {
    super();
    this._appSettings = new AppSettings();
  }

  @action public setLoggedInStatus(status: boolean): void {
    this.isLoggedIn = status;
  }

  @action public init(): void {
    if (this.getAuthContext()) {
      return;
    }

    this._appSettings = window.appSettings;

    const msalConfig = {
      auth: {
        clientId: this._appSettings.azureAppId,
        authority: this._appSettings.msLoginUrl + this._appSettings.azureTenantId,
        postLogoutRedirectUri: this._appSettings.postLogoutRedirectUri,
        redirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: this._appSettings.cacheLocation,
        storeAuthStateInCookie: false,
      }
    };
    const msalInstance = new PublicClientApplication(msalConfig);

    msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        msalInstance.setActiveAccount(account);
      }
    });

    this.setAuthContext(msalInstance);
    window.getToken = this.getToken;
    this.isLoggedIn = false;
  }

  // tslint:disable-next-line:no-any
  public getAuthContext(): any {
    return window.msalInstance;
  }

  // tslint:disable-next-line:no-any
  public getToken = async (): Promise<any> => {
    return new Promise((resolve, reject) => {
      const instance = this.getAuthContext();
      const activeAccount = instance.getActiveAccount();
      if (activeAccount !== null) {
        const accessTokenRequest = { scopes: [this._appSettings.scope], account: activeAccount } as SilentRequest;

        instance.acquireTokenSilent(accessTokenRequest)
          .then(({ accessToken }) => {
            resolve(accessToken);
          })
          .catch((error: Error) => {
            if (error) {
              this.log(StringResources.ErrorAcquiringToken, error);
              this.logout();
              reject(error);
              return;
            }
          });
      }
    });
  };

  public async handleRedirectCallback(): Promise<void> {
    if (this.isAuthenticated) {
      return;
    }

    const msalInstance = this.getAuthContext();
    const authResult = await msalInstance.handleRedirectPromise();
    if (authResult) {
      msalInstance.setActiveAccount(authResult.account);
      this.setLoggedInStatus(true);
    } else {
      await this.login();
    }
  }

  public async login(): Promise<void> {
    const msalInstance = this.getAuthContext();
    const loginRequest = { scopes: [this._appSettings.scope] };
    await msalInstance.loginRedirect(loginRequest);
    // login will jump page to reload, so we won't reach here
  }


  public logout = (): void => {
    if (!this.isAuthenticated) {
      return;
    }
    this.getAuthContext().logoutRedirect();
    this.setAuthContext(undefined);
    this.isLoggedIn = false;
  };

  public get isAuthenticated(): boolean {
    const account = this.getAuthContext()?.getActiveAccount();
    return account !== null;
  }

  // tslint:disable-next-line:no-any
  public get getUser(): { profile: any; username: string } | null {
    const account = this.getAuthContext()?.getActiveAccount() || null;
    return { profile: account?.idTokenClaims, username: account?.username || '' };
  }

  // tslint:disable-next-line:no-any
  private setAuthContext(msalInstance: any): void {
    window.msalInstance = msalInstance;
  }
}

export default AuthStore;
